import { useState } from "react";
import { useList } from "@refinedev/core";
import { FlaskConical } from "@/components/ui/icon";

import type { Event, Rule } from "@aos/shared";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

/**
 * Human-readable summary line for what a guard condition expects, derived from
 * the rule's DSL fields. Empty when no guard is configured.
 */
function guardSummary(rule: Rule): string[] {
  const parts: string[] = [];
  if (rule.artifact_type) parts.push(`artifact type = ${rule.artifact_type}`);
  if (rule.required_status) parts.push(`status = ${rule.required_status}`);
  if (rule.required_phase_id)
    parts.push(`phase = ${rule.required_phase_id}`);
  if (rule.required_evidence_type)
    parts.push(
      `evidence "${rule.required_evidence_type}"${
        rule.required_evidence_count
          ? ` count ≥ ${rule.required_evidence_count}`
          : " exists"
      }`,
    );
  return parts;
}

/** Human-readable summary line for the rule's effect. */
function effectSummary(rule: Rule): string {
  switch (rule.effect_type) {
    case "dispatch_skill":
      return `dispatch skill "${rule.target_skill_id ?? "?"}" via ${
        rule.executor_type ?? "default executor"
      }`;
    case "transition_status":
    case "update_artifact":
      return `set status → ${rule.target_status ?? "?"}`;
    case "create_task":
      return "create a follow-up task";
    case "create_evidence":
      return "create evidence";
    case "create_decision_request":
      return "request a decision";
    case "send_teams_message":
      return "send a Teams message";
    case "block":
      return "block the artifact";
    default:
      return rule.effect_type;
  }
}

/**
 * Dry-run preview for a rule. Opens a dialog summarizing the rule's trigger,
 * guard conditions, and effect, and reports how many recent events of the
 * rule's trigger type exist (i.e. would have been candidates). Read-only — it
 * never mutates state or dispatches effects.
 */
export function RuleDryRun({ rule }: { rule: Rule }) {
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useList<Event>({
    resource: "events",
    pagination: { pageSize: 1, mode: "server" },
    filters: [
      { field: "event_type", operator: "eq", value: rule.trigger_event_type },
    ],
    queryOptions: { enabled: open },
  });

  const candidateCount = data?.total ?? 0;
  const guards = guardSummary(rule);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <FlaskConical className="mr-2 h-4 w-4" />
          Dry run
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Dry run: {rule.name}</DialogTitle>
          <DialogDescription>
            Preview of how this rule would evaluate. No effects are executed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div>
            <p className="font-medium">Trigger</p>
            <p className="text-muted-foreground">
              On event <code>{rule.trigger_event_type}</code>
            </p>
          </div>
          <div>
            <p className="font-medium">Guard conditions</p>
            {guards.length === 0 ? (
              <p className="text-muted-foreground">
                None — matches every event of this type.
              </p>
            ) : (
              <ul className="ml-4 list-disc text-muted-foreground">
                {guards.map((g) => (
                  <li key={g}>{g}</li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <p className="font-medium">Effect</p>
            <p className="text-muted-foreground">{effectSummary(rule)}</p>
            {rule.emitted_event_type ? (
              <p className="text-muted-foreground">
                then emits <code>{rule.emitted_event_type}</code>
              </p>
            ) : null}
          </div>
          <div>
            <p className="font-medium">Candidate events</p>
            <p className="text-muted-foreground">
              {isLoading
                ? "Counting…"
                : `${candidateCount} recorded event${
                    candidateCount === 1 ? "" : "s"
                  } of type ${rule.trigger_event_type} could have matched.`}
            </p>
          </div>
          {rule.status !== "active" ? (
            <p className="rounded-md bg-muted p-2 text-xs text-muted-foreground">
              This rule is currently <strong>{rule.status}</strong> and will not
              fire until it is active.
            </p>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default RuleDryRun;
